import { apiFetch } from './client';
import type { Periodo } from './tienda.api';

/** El analista de anuncios. Prefijo: /api/publicidad (contrato version 1, igual en todos los Conversia). */

export type Veredicto = 'SE_QUEDA' | 'SE_VA' | 'VIGILAR' | 'FALTA_DATA';

export interface Cifras {
  leads: number;
  calientes: number;
  tibios: number;
  frios: number;
  sinCalificar: number;
  calidad: number | null;
  cerrados: number;
  tasaDeCierre: number | null;
  ventas: number;
  vendido: number;
  costo: number | null;
  costoPorLead: number | null;
  costoPorVenta: number | null;
  retorno: number | null;
}

export interface FilaDeAnuncio extends Cifras {
  sourceId: string | null;
  titulo: string;
  url: string | null;
  veredicto: Veredicto;
  motivo: string;
}

export interface Reporte {
  version: 1;
  negocio: string;
  sistema: string;
  moneda: string;
  desde: string;
  hasta: string;
  generadoAt: string;
  total: Cifras;
  anuncios: FilaDeAnuncio[];
  conclusiones: string[];
}

export function reporte(periodo: Periodo): Promise<Reporte> {
  return apiFetch<Reporte>('/api/publicidad', { query: { periodo } });
}

export function ponerCosto(body: { sourceId: string; titulo?: string | null; costo: number }): Promise<unknown> {
  return apiFetch('/api/publicidad/costo', { method: 'PUT', body });
}

export function enlace(): Promise<{ activo: boolean; creadoAt: string | null }> {
  return apiFetch('/api/publicidad/enlace');
}

export function crearEnlace(): Promise<{ token: string; ruta: string }> {
  return apiFetch('/api/publicidad/enlace', { method: 'POST' });
}

export function quitarEnlace(): Promise<{ activo: boolean }> {
  return apiFetch('/api/publicidad/enlace', { method: 'DELETE' });
}

const COLUMNAS: Array<[string, (a: FilaDeAnuncio) => unknown]> = [
  ['Anuncio', (a) => a.titulo],
  ['Id del anuncio', (a) => a.sourceId ?? ''],
  ['Veredicto', (a) => a.veredicto],
  ['Por qué', (a) => a.motivo],
  ['Chats', (a) => a.leads],
  ['Calientes', (a) => a.calientes],
  ['Tibios', (a) => a.tibios],
  ['Fríos', (a) => a.frios],
  ['Sin calificar', (a) => a.sinCalificar],
  ['Calidad (0-100)', (a) => a.calidad ?? ''],
  ['Cerrados', (a) => a.cerrados],
  ['Tasa de cierre %', (a) => a.tasaDeCierre ?? ''],
  ['Ventas', (a) => a.ventas],
  ['Vendido', (a) => a.vendido],
  ['Costo', (a) => a.costo ?? ''],
  ['Costo por chat', (a) => a.costoPorLead ?? ''],
  ['Costo por venta', (a) => a.costoPorVenta ?? ''],
  ['Retorno (veces)', (a) => a.retorno ?? ''],
];

/** Una fila por anuncio, para Excel (con BOM para que respete las tildes). */
export function aCsv(r: Reporte): string {
  const celda = (v: unknown) => {
    // Un título de anuncio no puede volverse fórmula en Excel.
    const s = String(v ?? '').replace(/^[=+\-@\t\r]/, "'$&");
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lineas = [COLUMNAS.map(([t]) => celda(t)).join(',')];
  for (const a of r.anuncios) lineas.push(COLUMNAS.map(([, f]) => celda(f(a))).join(','));
  return '﻿' + lineas.join('\r\n') + '\r\n';
}
